/**
 * Keyboard shortcuts definitions and editor UI.
 *
 * This module provides:
 * - Default shortcuts registry (command -> key binding)
 * - Shortcuts editor component for Settings
 * - Conflict detection
 * - Export/import functionality
 */

import { html, nothing } from "lit";
import { live } from "lit/directives/live.js";
import type { UiSettings } from "./storage.ts";

// Keyboard modifier and key names
export type KeyModifier = "ctrl" | "alt" | "shift" | "meta";

export interface KeyBinding {
  /** Primary key (e.g., 'a', 'Enter', 'F1') */
  key: string;
  /** Modifiers to hold (ctrl, alt, shift, meta) */
  modifiers?: KeyModifier[];
}

/** Human-readable key binding string (e.g., "Ctrl+K", "Alt+Shift+S") */
export function keyBindingToString(binding: KeyBinding | null | undefined): string {
  if (!binding) return "—";
  const parts: string[] = [];
  if (binding.modifiers) {
    for (const mod of binding.modifiers) {
      const label =
        mod === "ctrl" ? "Ctrl" : mod === "meta" ? "⌘" : mod === "alt" ? "Alt" : "Shift";
      parts.push(label);
    }
  }
  // Format special keys
  const key = binding.key;
  if (key === " ") parts.push("Space");
  else if (key.length === 1) parts.push(key.toUpperCase());
  else parts.push(key);
  return parts.join("+");
}

/** Parse a key binding string back to a KeyBinding object */
export function parseKeyBinding(str: string): KeyBinding | null {
  if (!str || str === "—") return null;
  const parts = str.split("+");
  const key = parts[parts.length - 1];
  const modifiers: KeyModifier[] = [];
  for (const part of parts.slice(0, -1)) {
    const lower = part.toLowerCase();
    if (lower === "ctrl") modifiers.push("ctrl");
    else if (lower === "alt") modifiers.push("alt");
    else if (lower === "shift") modifiers.push("shift");
    else if (lower === "meta" || lower === "⌘") modifiers.push("meta");
  }
  return { key, modifiers: modifiers.length > 0 ? modifiers : undefined };
}

// Default keyboard shortcuts
export interface ShortcutCommand {
  /** Unique command identifier */
  id: string;
  /** Human-readable command name */
  label: string;
  /** Category for grouping */
  category: string;
  /** Description shown in UI */
  description?: string;
  /** Default key binding */
  defaultBinding: KeyBinding | null;
}

export const DEFAULT_SHORTCUTS: ShortcutCommand[] = [
  // Chat commands
  {
    id: "chat.send",
    label: "Send Message",
    category: "Chat",
    description: "Send the current message",
    defaultBinding: { key: "Enter", modifiers: ["ctrl"] },
  },
  {
    id: "chat.newline",
    label: "New Line",
    category: "Chat",
    description: "Insert a new line in the message",
    defaultBinding: { key: "Enter", modifiers: ["shift"] },
  },
  {
    id: "chat.clear",
    label: "Clear Input",
    category: "Chat",
    description: "Clear the message input",
    defaultBinding: { key: "Escape" },
  },

  // Navigation commands
  {
    id: "nav.chat",
    label: "Go to Chat",
    category: "Navigation",
    description: "Switch to Chat tab",
    defaultBinding: { key: "1", modifiers: ["ctrl"] },
  },
  {
    id: "nav.overview",
    label: "Go to Overview",
    category: "Navigation",
    description: "Switch to Overview tab",
    defaultBinding: { key: "2", modifiers: ["ctrl"] },
  },
  {
    id: "nav.channels",
    label: "Go to Channels",
    category: "Navigation",
    description: "Switch to Channels tab",
    defaultBinding: { key: "3", modifiers: ["ctrl"] },
  },
  {
    id: "nav.agents",
    label: "Go to Agents",
    category: "Navigation",
    description: "Switch to Agents tab",
    defaultBinding: { key: "4", modifiers: ["ctrl"] },
  },
  {
    id: "nav.config",
    label: "Go to Config",
    category: "Navigation",
    description: "Switch to Config tab",
    defaultBinding: { key: "5", modifiers: ["ctrl"] },
  },

  // Fleet Control commands
  {
    id: "fleet.toggleHistory",
    label: "Toggle History Sidebar",
    category: "Fleet Control",
    description: "Show/hide the chat history sidebar",
    defaultBinding: { key: "h", modifiers: ["ctrl"] },
  },
  {
    id: "fleet.toggleInspector",
    label: "Toggle Inspector",
    category: "Fleet Control",
    description: "Show/hide the session inspector",
    defaultBinding: { key: "i", modifiers: ["ctrl"] },
  },
  {
    id: "fleet.togglePanels",
    label: "Toggle All Panels",
    category: "Fleet Control",
    description: "Expand/collapse all panels",
    defaultBinding: { key: "m", modifiers: ["ctrl"] },
  },
  {
    id: "fleet.focusComposer",
    label: "Focus Composer",
    category: "Fleet Control",
    description: "Focus the message composer",
    defaultBinding: { key: "/", modifiers: ["ctrl"] },
  },

  // Composer mode cycling
  {
    id: "fleet.modeAgent",
    label: "Set Agent Mode",
    category: "Fleet Control",
    description: "Switch composer to Agent mode",
    defaultBinding: { key: "a", modifiers: ["ctrl"] },
  },
  {
    id: "fleet.modeAsk",
    label: "Set Ask Mode",
    category: "Fleet Control",
    description: "Switch composer to Ask mode",
    defaultBinding: { key: "q", modifiers: ["ctrl"] },
  },
  {
    id: "fleet.modePlan",
    label: "Set Plan Mode",
    category: "Fleet Control",
    description: "Switch composer to Plan mode",
    defaultBinding: { key: "p", modifiers: ["ctrl"] },
  },
  {
    id: "fleet.toggleMultiAgent",
    label: "Multi-Agent Switcher",
    category: "Fleet Control",
    description: "Open the multi-agent profile quick-switcher popover",
    defaultBinding: { key: "m", modifiers: ["ctrl"] },
  },

  // Session commands
  {
    id: "session.next",
    label: "Next Session",
    category: "Sessions",
    description: "Switch to next chat session",
    defaultBinding: { key: "]", modifiers: ["ctrl"] },
  },
  {
    id: "session.prev",
    label: "Previous Session",
    category: "Sessions",
    description: "Switch to previous chat session",
    defaultBinding: { key: "[", modifiers: ["ctrl"] },
  },
  {
    id: "session.new",
    label: "New Session",
    category: "Sessions",
    description: "Create a new chat session",
    defaultBinding: { key: "t", modifiers: ["ctrl"] },
  },
  {
    id: "session.close",
    label: "Close Session",
    category: "Sessions",
    description: "Close the current session",
    defaultBinding: { key: "w", modifiers: ["ctrl"] },
  },

  // Search
  {
    id: "search.global",
    label: "Global Search",
    category: "Search",
    description: "Open global search",
    defaultBinding: { key: "k", modifiers: ["ctrl"] },
  },
  {
    id: "search.chat",
    label: "Search Chat",
    category: "Search",
    description: "Search within current chat",
    defaultBinding: { key: "f", modifiers: ["ctrl"] },
  },

  // Help
  {
    id: "help.shortcuts",
    label: "Show Shortcuts",
    category: "Help",
    description: "Display keyboard shortcuts reference",
    defaultBinding: { key: "?", modifiers: ["shift"] },
  },
  {
    id: "help.toggle",
    label: "Toggle Help",
    category: "Help",
    description: "Show or hide help panel",
    defaultBinding: { key: "F1" },
  },
];

// Get categories from shortcuts
export function getShortcutCategories(shortcuts: ShortcutCommand[]): string[] {
  const categories = new Set(shortcuts.map((s) => s.category));
  return Array.from(categories).sort();
}

// Group shortcuts by category
export function groupShortcutsByCategory(
  shortcuts: ShortcutCommand[],
): Map<string, ShortcutCommand[]> {
  const grouped = new Map<string, ShortcutCommand[]>();
  for (const shortcut of shortcuts) {
    const existing = grouped.get(shortcut.category) ?? [];
    existing.push(shortcut);
    grouped.set(shortcut.category, existing);
  }
  return grouped;
}

// Check for key binding conflicts
export function findConflicts(
  bindings: Map<string, KeyBinding | null>,
): Array<{ binding: KeyBinding; commands: string[] }> {
  const conflicts = new Map<string, string[]>();

  for (const [commandId, binding] of bindings) {
    if (!binding) continue;
    const key = keyBindingToString(binding);
    const existing = conflicts.get(key) ?? [];
    existing.push(commandId);
    conflicts.set(key, existing);
  }

  return Array.from(conflicts.entries())
    .filter(([_, commands]) => commands.length > 1)
    .map(([key, commands]) => ({
      binding: parseKeyBinding(key)!,
      commands,
    }));
}

// Shortcuts storage key
const SHORTCUTS_STORAGE_KEY = "openclaw.control.shortcuts.v1";

export type ShortcutsConfig = Record<string, KeyBinding | null>;

/** Load saved shortcuts from localStorage */
export function loadShortcuts(): ShortcutsConfig {
  try {
    const raw = localStorage.getItem(SHORTCUTS_STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as ShortcutsConfig;
  } catch {
    return {};
  }
}

/** Save shortcuts to localStorage */
export function saveShortcuts(config: ShortcutsConfig): void {
  localStorage.setItem(SHORTCUTS_STORAGE_KEY, JSON.stringify(config));
}

/** Get effective key binding (custom or default) */
export function getEffectiveBinding(
  commandId: string,
  customBindings: ShortcutsConfig,
  defaults: ShortcutCommand[],
): KeyBinding | null {
  if (commandId in customBindings) {
    return customBindings[commandId];
  }
  const default_ = defaults.find((s) => s.id === commandId);
  return default_?.defaultBinding ?? null;
}

/** Export shortcuts to JSON string */
export function exportShortcuts(config: ShortcutsConfig): string {
  return JSON.stringify({ version: 1, shortcuts: config }, null, 2);
}

/** Import shortcuts from JSON string */
export function importShortcuts(json: string): ShortcutsConfig | null {
  try {
    const parsed = JSON.parse(json);
    if (!parsed.shortcuts || typeof parsed.shortcuts !== "object") {
      return null;
    }
    return parsed.shortcuts as ShortcutsConfig;
  } catch {
    return null;
  }
}

// ============================================================
// Keyboard Shortcuts Editor Component
// ============================================================

export interface KeyboardShortcutsEditorProps {
  shortcuts: ShortcutCommand[];
  customBindings: ShortcutsConfig;
  onBindingChange: (commandId: string, binding: KeyBinding | null) => void;
  onResetToDefault: (commandId: string) => void;
  onResetAll: () => void;
  onExport: () => void;
  onImport: (config: ShortcutsConfig) => void;
}

export function renderKeyboardShortcutsEditor(props: KeyboardShortcutsEditorProps) {
  const {
    shortcuts,
    customBindings,
    onBindingChange,
    onResetToDefault,
    onResetAll,
    onExport,
    onImport,
  } = props;

  // Group shortcuts by category
  const categories = getShortcutCategories(shortcuts);
  const grouped = groupShortcutsByCategory(shortcuts);

  // Build bindings map for conflict detection
  const bindingsMap = new Map<string, KeyBinding | null>();
  for (const shortcut of shortcuts) {
    bindingsMap.set(shortcut.id, getEffectiveBinding(shortcut.id, customBindings, shortcuts));
  }
  const conflicts = findConflicts(bindingsMap);
  const conflictingCommands = new Set(conflicts.flatMap((c) => c.commands));

  // State for rebinding
  let rebindingCommandId: string | null = null;
  let rebindingKey: string | null = null;

  const handleKeyCapture = (commandId: string) => (e: KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Escape cancels rebinding
    if (e.key === "Escape") {
      rebindingCommandId = null;
      rebindingKey = null;
      return;
    }

    // Ignore modifier-only presses
    if (["Control", "Alt", "Shift", "Meta"].includes(e.key)) {
      return;
    }

    const modifiers: KeyModifier[] = [];
    if (e.ctrlKey) modifiers.push("ctrl");
    if (e.altKey) modifiers.push("alt");
    if (e.shiftKey) modifiers.push("shift");
    if (e.metaKey) modifiers.push("meta");

    const binding: KeyBinding = {
      key: e.key,
      modifiers: modifiers.length > 0 ? modifiers : undefined,
    };

    onBindingChange(commandId, binding);
    rebindingCommandId = null;
    rebindingKey = null;
  };

  return html`
    <div class="shortcuts-editor">
      <!-- Header with actions -->
      <div class="shortcuts-editor__header">
        <div class="shortcuts-editor__title">
          <h3>Keyboard Shortcuts</h3>
          <p class="muted">Click a shortcut to rebind it. Press Escape to cancel.</p>
        </div>
        <div class="shortcuts-editor__actions">
          <button class="btn btn--sm" @click=${onExport}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            Export
          </button>
          <label class="btn btn--sm">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="17 8 12 3 7 8"></polyline>
              <line x1="12" y1="3" x2="12" y2="15"></line>
            </svg>
            Import
            <input type="file" accept=".json" style="display: none" @change=${(e: Event) => {
              const input = e.target as HTMLInputElement;
              const file = input.files?.[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = () => {
                const config = importShortcuts(reader.result as string);
                if (config) {
                  onImport(config);
                }
              };
              reader.readAsText(file);
            }} />
          </label>
          <button class="btn btn--sm" @click=${onResetAll}>
            Reset All
          </button>
        </div>
      </div>

      <!-- Conflict warnings -->
      ${
        conflicts.length > 0
          ? html`
        <div class="shortcuts-editor__conflicts">
          <div class="callout warning">
            <strong>Conflicts detected:</strong>
            ${conflicts.map(
              (c) => html`
              <div>
                <code>${keyBindingToString(c.binding)}</code> is assigned to:
                ${c.commands.map(
                  (cmd, i) => html`
                  ${i > 0 ? ", " : ""}${shortcuts.find((s) => s.id === cmd)?.label ?? cmd}
                `,
                )}
              </div>
            `,
            )}
          </div>
        </div>
      `
          : nothing
      }

      <!-- Shortcuts by category -->
      <div class="shortcuts-editor__categories">
        ${categories.map(
          (category) => html`
          <div class="shortcuts-category">
            <h4 class="shortcuts-category__title">${category}</h4>
            <div class="shortcuts-category__list">
              ${(grouped.get(category) ?? []).map((shortcut) => {
                const effective = getEffectiveBinding(shortcut.id, customBindings, shortcuts);
                const hasConflict = conflictingCommands.has(shortcut.id);
                const isDefault = !(shortcut.id in customBindings);
                const isRebinding = rebindingCommandId === shortcut.id;

                return html`
                  <div class="shortcut-row ${hasConflict ? "shortcut-row--conflict" : ""}">
                    <div class="shortcut-row__info">
                      <span class="shortcut-row__label">${shortcut.label}</span>
                      ${
                        shortcut.description
                          ? html`
                        <span class="shortcut-row__desc muted">${shortcut.description}</span>
                      `
                          : nothing
                      }
                    </div>
                    <div class="shortcut-row__binding">
                      ${
                        isRebinding
                          ? html`
                              <span class="shortcut-key shortcut-key--listening"> Press a key... </span>
                            `
                          : html`
                        <button
                          class="shortcut-key ${hasConflict ? "shortcut-key--conflict" : ""} ${effective ? "" : "shortcut-key--empty"}"
                          @click=${() => {
                            rebindingCommandId = shortcut.id;
                          }}
                          @keydown=${handleKeyCapture(shortcut.id)}
                          title="Click to rebind"
                        >
                          ${effective ? keyBindingToString(effective) : "Unbound"}
                        </button>
                      `
                      }
                      ${
                        !isDefault
                          ? html`
                        <button
                          class="shortcut-reset"
                          @click=${() => onResetToDefault(shortcut.id)}
                          title="Reset to default"
                        >
                          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                            <path d="M3 3v5h5"></path>
                          </svg>
                        </button>
                      `
                          : nothing
                      }
                    </div>
                  </div>
                `;
              })}
            </div>
          </div>
        `,
        )}
      </div>
    </div>
  `;
}

// ============================================================
// Styles (injected via CSS)
// ============================================================

export const KEYBOARD_SHORTCUTS_STYLES = `
.shortcuts-editor {
  padding: 16px;
}

.shortcuts-editor__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  gap: 16px;
}

.shortcuts-editor__title h3 {
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
}

.shortcuts-editor__title p {
  margin: 0;
  font-size: 13px;
}

.shortcuts-editor__actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.shortcuts-editor__conflicts {
  margin-bottom: 16px;
}

.shortcuts-category {
  margin-bottom: 24px;
}

.shortcuts-category__title {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--color-muted, #6b7280);
  margin: 0 0 8px 0;
  padding-bottom: 4px;
  border-bottom: 1px solid var(--color-border, #e5e7eb);
}

.shortcuts-category__list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.shortcut-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-radius: 6px;
  transition: background-color 0.15s;
}

.shortcut-row:hover {
  background-color: var(--color-surface-hover, rgba(0, 0, 0, 0.03));
}

.shortcut-row--conflict {
  background-color: rgba(251, 146, 60, 0.1);
}

.shortcut-row__info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.shortcut-row__label {
  font-size: 14px;
  font-weight: 500;
}

.shortcut-row__desc {
  font-size: 12px;
}

.shortcut-row__binding {
  display: flex;
  align-items: center;
  gap: 6px;
}

.shortcut-key {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 80px;
  padding: 4px 10px;
  font-family: ui-monospace, monospace;
  font-size: 12px;
  font-weight: 500;
  background: var(--color-surface, #f9fafb);
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s;
}

.shortcut-key:hover {
  background: var(--color-surface-hover, #f3f4f6);
  border-color: var(--color-primary, #3b82f6);
}

.shortcut-key:focus {
  outline: 2px solid var(--color-primary, #3b82f6);
  outline-offset: 2px;
}

.shortcut-key--empty {
  color: var(--color-muted, #9ca3af);
  font-style: italic;
}

.shortcut-key--conflict {
  border-color: #f97316;
  background: rgba(251, 146, 60, 0.1);
}

.shortcut-key--listening {
  background: var(--color-primary, #3b82f6);
  color: white;
  border-color: var(--color-primary, #3b82f6);
  animation: pulse 1s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

.shortcut-reset {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: var(--color-muted, #9ca3af);
  cursor: pointer;
  transition: all 0.15s;
}

.shortcut-reset:hover {
  background: var(--color-surface-hover, #f3f4f6);
  color: var(--color-text, #374151);
}

/* Dark theme adjustments */
[data-theme="dark"] .shortcut-key {
  background: var(--color-surface, #1f2937);
  border-color: var(--color-border, #374151);
}

[data-theme="dark"] .shortcut-key:hover {
  background: var(--color-surface-hover, #374151);
}

[data-theme="dark"] .shortcut-reset:hover {
  background: var(--color-surface-hover, #374151);
}

[data-theme="dark"] .shortcut-row:hover {
  background-color: var(--color-surface-hover, rgba(255, 255, 255, 0.03));
}
`;
